import React, { useEffect } from 'react'
import { useFormikContext } from 'formik'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'

import SimpleInput from 'app/componentV4/Input/SimpleInput'
import LabelInput from 'app/componentV4/Labels/LabelInput'
import { makeStyles, useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import ContainedButton from 'app/componentV4/Button/ContainedButton'
import { CLOSE, SECONDARY, CANCEL } from 'app/constants/button'
import {
  openWarningDialog,
  closeWarningDialog,
} from 'app/store/actions/warningDialog.actions'

import clsx from 'clsx'

const useStyles = makeStyles((theme) => ({
  formRoot: {
    '& .MuiFormControl-root': {
      '& .label-error': {
        marginTop: '4px',
      },
    },
  },
  inputFormat: {
    '& .MuiInputBase-root': {
      color: 'rgb(24 53 96 / var(--tw-text-opacity)) !important',
    },
    '& fieldset': {
      height: '53px !important',
      borderRadius: '.8rem !important',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 17px) scale(1)',
    },
  },
  labelInput: {
    '& span': {
      color: '#EF4444',
    },
  },
}))

const EmployerInformationForm = ({
  subscriberId,
  onEmployerChange,
  onEmployerReset,
  refetchData,
}) => {
  const formik = useFormikContext()

  const classes = useStyles()
  const { formatMessage } = useIntl()
  const isButtonDisabled = !formik.dirty || formik.isValidating
  const isButtonLoading = formik.isValidating || formik.isSubmitting
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'))
  const dispatch = useDispatch()

  const onCancelClick = () => {
    if (formik.dirty) {
      const payload = {
        title: formatMessage({ id: 'dialog.discard.title' }),
        description: formatMessage({ id: 'dialog.discard.body' }),
        submitText: formatMessage({
          id: 'draft.notification.create.popup.title',
        }),
        closeText: formatMessage({ id: 'dialog.discard.close' }),
        type: CANCEL,
        onSubmit: () => {
          formik.resetForm()
          onEmployerReset()
          refetchData(subscriberId)
          dispatch(closeWarningDialog())
        },
        onClose: () => {
          dispatch(closeWarningDialog())
        },
      }
      dispatch(openWarningDialog(payload))
    }
  }

  useEffect(() => {
    if (formik.dirty) {
      onEmployerChange(formik.dirty)
    } else {
      onEmployerReset()
    }
  }, [formik.dirty])

  return (
    <div className={clsx('w-full', classes.formRoot)}>
      <div className={isDownXs ? 'space-x-0' : 'flex space-x-10'}>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.companyName',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="companyName"
            name="companyName"
            type="text"
            value={formik.values.companyName}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(event) => {
              formik.setFieldValue('companyName', event.target.value)
              formik.setFieldTouched('companyName', true, false)
            }}
            placeholder={formatMessage({
              id: 'crm.form.companyName',
            })}
            error={formik.errors.companyName && formik.touched.companyName}
            helperText={formik.errors.companyName}
          />
        </LabelInput>
        <LabelInput
          label={formatMessage({
            id: 'crm.form.jobTitle',
          })}
          className="w-full capitalize"
        >
          <SimpleInput
            id="jobTitle"
            name="jobTitle"
            type="text"
            value={formik.values.jobTitle}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(e) => {
              formik.setFieldValue('jobTitle', e.target.value)
              formik.setFieldTouched('jobTitle', true, false)
            }}
            placeholder={formatMessage({
              id: 'crm.form.jobTitle',
            })}
            error={formik.errors.jobTitle && formik.touched.jobTitle}
            helperText={formik.errors.jobTitle}
          />
        </LabelInput>
      </div>
      <div
        className={clsx(
          isDownXs ? 'space-x-0 w-full' : 'flex space-x-10',
          'w-1/2 pr-5',
        )}
      >
        <LabelInput
          label={formatMessage({
            id: 'subscriber.label.jobDepartment',
          })}
        >
          <SimpleInput
            id="jobDepartment"
            name="jobDepartment"
            type="text"
            value={formik.values.jobDepartment}
            className="h-48 w-full mb-1 sm:mb-0 bg-white rounded-8 text-blue-950"
            onChange={(event) => {
              formik.setFieldValue('jobDepartment', event.target.value)
              formik.setFieldTouched('jobDepartment', true, false)
            }}
            placeholder={formatMessage({
              id: 'subscriber.label.jobDepartment',
            })}
            error={formik.errors.jobDepartment && formik.touched.jobDepartment}
            helperText={formik.errors.jobDepartment}
          />
        </LabelInput>
      </div>
      <div className="flex justify-end space-x-5 mt-5">
        <ContainedButton
          color={CLOSE}
          onClick={onCancelClick}
          disabled={isButtonDisabled || isButtonLoading}
          type="button"
          className="w-96"
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'action.cancel',
              })}
            </span>
          </div>
        </ContainedButton>
        <ContainedButton
          color={SECONDARY}
          isLoading={isButtonLoading}
          disabled={isButtonDisabled || isButtonLoading}
          type="submit"
        >
          <div className="flex">
            <span className="normal-case">
              {formatMessage({
                id: 'subscriberCrm.editGroup.save.label',
              })}
            </span>
          </div>
        </ContainedButton>
      </div>
    </div>
  )
}

export default EmployerInformationForm
